import { Divider, Stack, useTheme } from '@mui/material';
import '@xyflow/react/dist/style.css';
import { FC, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthSnackbar from './components/common/auth/AuthSnackbar';
import EmptyProjectPlaceholder from './components/EmptyProjectPlaceholder';
import FlowMap from './components/flow/FlowMap';
import NodeSettingsList from './components/nodes/settings/NodeSettingsList';
import TreeMenu from './components/treemenu/TreeMenu';
import ValidatorBar from './components/validation/ValidatorBar';
import { useApplication } from './hooks/useApplication';
import { useDialogs } from './hooks/useDialogs';
import { useSubroutine } from './hooks/useSubroutine';
import { useViewportContext } from './hooks/useViewport';
import { SelectNode } from './types/backendNode';
import { OpenEditType } from './types/subroutine';

type IDEProps = {
  openEdit: OpenEditType;
  setOpenEdit: (openEdit: OpenEditType) => void;
  selectedNodeIdsSettings: SelectNode[];
  setSelectedNodeIdsSettings: (newSettings: SelectNode[]) => void;
};

const IDE: FC<IDEProps> = (props) => {
  const theme = useTheme();
  const { application } = useApplication();
  const { selectedSubroutineId } = useSubroutine();
  const { confirm } = useDialogs();
  const refSettingsNodes = useRef<SelectNode[]>([]);
  const { setCenterOnNode } = useViewportContext();

  const [searchParams] = useSearchParams();

  const handleNodeDetailsClicked = (nodeId: number) => {
    const { current: nodeIds = [] } = refSettingsNodes;
    const selectedNodeIds: SelectNode[] = nodeIds.some((node) => node.id === nodeId)
      ? nodeIds.filter((node) => node.id !== nodeId)
      : [
          ...nodeIds.filter((node) => node.isPinned),
          {
            id: nodeId,
            parentId: selectedSubroutineId || 0,
            isPinned: false
          }
        ];
    props.setSelectedNodeIdsSettings(selectedNodeIds);
  };

  useEffect(() => {
    refSettingsNodes.current = props.selectedNodeIdsSettings;
  }, [props.selectedNodeIdsSettings]);

  useEffect(() => {
    const appname = searchParams.get('appname');
    const blockid = searchParams.get('blockid');
    if (!blockid || isNaN(Number(blockid))) return;

    if (appname && appname.toLocaleLowerCase() !== application.name.toLocaleLowerCase()) {
      confirm({
        title: 'Appname mismatch',
        description: `Appname (${appname}) doesn't match the current application's name (${application.name})`,
        confirmationText: 'Ok',
        hideCancelButton: true
      });
      return;
    }

    setCenterOnNode(blockid);
  }, [searchParams]);

  if (!selectedSubroutineId) return <EmptyProjectPlaceholder />;

  return (
    <>
      <Stack
        direction='row'
        // height={`calc(100vh - (${theme.mixins.toolbar.minHeight}px - ${8}px))`}
        minHeight={0}
        height='100%'
        bgcolor={theme.palette.background.default}
      >
        <TreeMenu openEdit={props.openEdit} setOpenEdit={props.setOpenEdit} />
        <Divider orientation='vertical' />
        <FlowMap onNodeDetailsClicked={handleNodeDetailsClicked} />
        <Divider orientation='vertical' />
        {props.selectedNodeIdsSettings.length > 0 && (
          <NodeSettingsList
            nodes={props.selectedNodeIdsSettings}
            pinNode={(isPinned, node) => {
              props.setSelectedNodeIdsSettings(
                props.selectedNodeIdsSettings.map((selectedNode) =>
                  selectedNode.id === node.id ? { ...selectedNode, isPinned: isPinned } : selectedNode
                )
              );
            }}
            onClose={(node) =>
              props.setSelectedNodeIdsSettings(props.selectedNodeIdsSettings.filter((n) => n.id !== node.id))
            }
          />
        )}
        <AuthSnackbar message='Log in to unlock more features' position={{ vertical: 'bottom', horizontal: 'left' }} />
      </Stack>
      <ValidatorBar />
    </>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export default IDE;
